import { LazyQueryHookOptions, QueryTuple } from "@apollo/react-hooks";
import { WatchQueryFetchPolicy } from "apollo-client";
import { debounce, get } from "lodash";
import { useEffect, useState } from "react";

import { GQLQueryUpdateHelper } from "~/libs/GQLQueryUpdateHelper";

interface IProps<ResponseData, ListItemsType> {
  queryGenerator: (baseOptions?: LazyQueryHookOptions) => QueryTuple<any, any>;
  responseDataGetterString: string;

  additionQueryParameters?: { [key: string]: any };
  customValueHandler?: (element: ResponseData) => {
    label: string;
    value: string | number;
  };
  customDecoratedValueHandler?: (element: ResponseData) => ListItemsType;
  searchConfig?: (search: string) => { [key: string]: any };
  fetchPolicy?: WatchQueryFetchPolicy;
  count?: number;
  asLazy?: boolean;
}

export const useSelectorDataConfigurator = <
  ResponseData,
  ListItemsType = { label: string; value: string | number },
>(
  props: IProps<ResponseData, ListItemsType>,
) => {
  /**
   * @param queryGenerator - ссылка на функцию запроса LazyQuery
   * @param responseDataGetterString - Путь до списка в виде строки (пример: "DealerGetter.getDealers.items")
   * @param customValueHandler - Кастомная обработка значений пришедших с запроса со строго заданной возвратной сигнатурой
   * @param customDecoratedValueHandler - Такая же кастомная обработка как вышеуказанная, но возвратная сигнатура определяется дженериком ListItemsType
   * @param searchConfig - Конфигурация поиска. Получает строку поиска, возвращает объект который, прокидывается в variables запроса
   * @param additionQueryParameters - Дополнительные параметры запроса, если необходимы.
   * @param fetchPolicy - Сетевая политика запроса.
   * @param count - Количество получаемых элементов.
   * */
  const {
    queryGenerator,
    responseDataGetterString,
    customValueHandler,
    customDecoratedValueHandler,
    searchConfig,
    additionQueryParameters = {},
    fetchPolicy = "cache-and-network",
    count = 25,
    asLazy = false,
  } = props;

  const [getDataForSelectQuery, { data, loading, fetchMore }] = queryGenerator({
    variables: {
      ...additionQueryParameters,
      count,
      page: 0,
    },
    fetchPolicy,
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!asLazy) {
      getDataForSelectQuery();
    }
  }, [asLazy]);

  const standardValueHandler = ({ id, name }) => {
    return {
      value: id,
      label: name,
    };
  };

  const [rowsForSelect, setRowsForSelect] = useState<ListItemsType[]>([]);

  useEffect(() => {
    setRowsForSelect([
      ...get(data, responseDataGetterString, []).map(
        customValueHandler ||
          customDecoratedValueHandler ||
          standardValueHandler,
      ),
    ]);
  }, [data]);

  const introspectionHandler = () => {
    if (rowsForSelect.length < count || loading) {
      return;
    }
    const page = Math.ceil(rowsForSelect.length / count);
    fetchMore({
      variables: {
        page,
        count,
      },
      updateQuery: (Getter, { fetchMoreResult }) => {
        const keys = responseDataGetterString.split(".");
        if (!fetchMoreResult) return Getter;
        else
          return GQLQueryUpdateHelper(keys, Getter, {
            fetchMoreResult,
            responseDataGetterString,
          });
      },
    }).then(({ data }) => {
      // updateQuery уже смержил страницу в data (см. эффект на [data] выше),
      // поэтому дописываем только те элементы, которых ещё нет в списке,
      // иначе страница дублируется и ломает селект (дубли value как React-ключей).
      const newItems = get(data, responseDataGetterString, []).map(
        customValueHandler ||
          customDecoratedValueHandler ||
          standardValueHandler,
      );
      setRowsForSelect((p) => {
        const present = new Set(
          p.map((item) => (item as { value?: unknown })?.value),
        );
        return [
          ...p,
          ...newItems.filter((item) => {
            const value = (item as { value?: unknown })?.value;
            return value === undefined || !present.has(value);
          }),
        ];
      });
    });
  };

  useEffect(() => {
    if (fetchMore) introspectionHandler();
  }, [fetchMore]);

  const searchValue = debounce((search: string) => {
    getDataForSelectQuery({
      variables: {
        ...additionQueryParameters,
        ...(searchConfig ? searchConfig(search) : {}),
        count,
        page: 0,
      },
    });
  }, 0);

  return {
    rowsForSelect,
    searchValue,
    introspectionHandler,
    dataRequestLoading: !!loading,
    ...(asLazy ? { getter: getDataForSelectQuery } : {}),
  };
};
